/**
 * Feature flag constants used across the app.
 */
export const FEATURE_FLAGS = {
  USER_MANAGEMENT: "user-management",
  UPLOADED_AUDIO_CHANNEL_SELECTION: "uploaded-audio-channel-selection",
  DASHBOARD_P50: "dashboard-p50",
  COVAL_ONLY: "coval-only",
  CUSTOM_WRAPPER_OBSERVATIONS: "chime-wrapper-observations",
  API_SIMULATION_WRAPPER: "api-simulation-wrapper",
  BENCHMARKS: "benchmarks",
  AGENT_MUTATIONS: "agent-mutations",
  AUTO_TEST_SET_GENERATION: "auto-test-set-generation",
  RUBRIC_DECISION_TREE: "rubric-decision-tree",
  SDK_WEBSOCKET_AGENT: "sdk-websocket-agent",
  EVAL_GRAPHS: "eval-graphs",
  LLM_JUDGE_JEV_CALIBRATION: "llm-judge-jev-calibration",
  AGENT_JUDGE_METRIC: "agent-judge-metric",
  CONVERSATION_CLUSTER_VISUALIZER: "conversation-cluster-visualizer",
  TRANSCRIPT_TOOL_CALL_NAMES: "transcript-tool-call-names",
  PROJECT_INSIGHTS_TILE_GRID: "insights-tile-grid",
  HUMAN_REVIEW_COMPARISON_INSIGHTS: "human-review-comparison-insights",
  HUMAN_REVIEW_ROLE_MODES: "human-review-role-modes",
  HUMAN_REVIEW_BATCHES: "human-review-batches",
  SIMULATION_ROOMS: "simulation-rooms",
  OPENAI_GPT_LIVE_ALPHA: "openai-gpt-live-alpha",
  SOFIA_NOTEBOOKS: "sofia-notebooks",
  SOFIA_SLACK: "sofia-slack",
  SOFIA_GITHUB_CONNECTIONS: "sofia-github-connections",
  GENESYS_CONVERSATION_MONITORING: "genesys-conversation-monitoring",
  PERSONA_CUSTOM_VOICE_DESIGN: "persona-custom-voice-design",
  METRIC_AUDIT: "metric-audit",
  PROMPTED_INTERRUPTIONS: "prompted-interruptions",
  GLOBAL_NAVIGATION_INDICATOR: "global-navigation-indicator",
} as const

/**
 * Local dev overrides for feature flags.
 * These bypass PostHog in development, so you don't need PostHog enabled.
 * Only flags explicitly set here will be overridden - others fall through to PostHog.
 */
export const LOCAL_DEV_FLAGS: Partial<Record<string, boolean>> = {
  // Internal dev-only tooling is gated by the coval-only flag in production
  // (PostHog targets Coval users); local dev keeps it on without PostHog.
  [FEATURE_FLAGS.COVAL_ONLY]: true,
  [FEATURE_FLAGS.API_SIMULATION_WRAPPER]: true,
  [FEATURE_FLAGS.BENCHMARKS]: true,
  [FEATURE_FLAGS.AUTO_TEST_SET_GENERATION]: true,
  [FEATURE_FLAGS.RUBRIC_DECISION_TREE]: true,
  [FEATURE_FLAGS.EVAL_GRAPHS]: true,
  // Agent Judge is rolled out per organization through PostHog; local development
  // keeps the authoring flow available without a PostHog project.
  [FEATURE_FLAGS.AGENT_JUDGE_METRIC]: true,
  // Tool call names inline in transcripts ship dark in production (PostHog
  // per-org rollout); local dev keeps it on without PostHog.
  [FEATURE_FLAGS.TRANSCRIPT_TOOL_CALL_NAMES]: true,
  // User Management has a limited PostHog audience; local development keeps
  // the settings surface available for UI iteration without PostHog enabled.
  [FEATURE_FLAGS.USER_MANAGEMENT]: true,
  // The Project Insights tile grid ships dark in production (PostHog per-org
  // rollout); local dev keeps it on without PostHog.
  [FEATURE_FLAGS.PROJECT_INSIGHTS_TILE_GRID]: true,
  // Human Review comparison insights ship dark in production (PostHog per-org
  // rollout); local dev keeps the aggregate-backed widgets on for validation.
  [FEATURE_FLAGS.HUMAN_REVIEW_COMPARISON_INSIGHTS]: true,
  // The split Human Review landing pages ship dark in production while the
  // assignee and manager workflows are validated together.
  [FEATURE_FLAGS.HUMAN_REVIEW_ROLE_MODES]: true,
  // Simulation rooms ship dark in production (PostHog per-org rollout) while
  // LiveKit is in vendor review; local dev keeps them on without PostHog.
  [FEATURE_FLAGS.SIMULATION_ROOMS]: true,
  // The confidential OpenAI alpha is targeted to approved organizations in
  // PostHog. Local development keeps the selector available for validation.
  [FEATURE_FLAGS.OPENAI_GPT_LIVE_ALPHA]: true,
  // Sofia notebooks are initially targeted at Coval users in PostHog; local
  // development keeps them on without PostHog.
  [FEATURE_FLAGS.SOFIA_NOTEBOOKS]: true,
  // Sofia-in-Slack remains dark in production until an explicit PostHog
  // audience is configured. Local development keeps the gated UI available
  // for cal-demo-dev validation.
  [FEATURE_FLAGS.SOFIA_SLACK]: true,
  [FEATURE_FLAGS.SOFIA_GITHUB_CONNECTIONS]: true,
  // Genesys conversation monitoring remains dark in production while the
  // connector is validated with the Genesys partner lab.
  [FEATURE_FLAGS.GENESYS_CONVERSATION_MONITORING]: true,
  [FEATURE_FLAGS.PERSONA_CUSTOM_VOICE_DESIGN]: true,
  [FEATURE_FLAGS.METRIC_AUDIT]: true,
  // The persona interruption-instructions toggle ships dark in production
  // (PostHog per-org rollout); local dev keeps it on without PostHog.
  [FEATURE_FLAGS.PROMPTED_INTERRUPTIONS]: true,
  // The top-of-page navigation progress bar ships dark in production (PostHog rollout); local dev keeps it on without PostHog.
  [FEATURE_FLAGS.GLOBAL_NAVIGATION_INDICATOR]: true,
}
